import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from '@/components/auth/Login';
import Registration from '@/components/auth/Registration';
import ResetPassword from '@/components/auth/ResetPassword';
import LandingPage from '@/components/landing/LandingPage';
import FrauMeetsBoersePage from '@/components/frauMeetsBoerse/FrauMeetsBoersePage';
import { FmbSubRoutes } from '@/components/frauMeetsBoerse/FmbHostRouter';
import { FRAU_MEETS_BOERSE_CONFIG } from '@/consts/frauMeetsBoerse';
import ImpressumPage from '@/components/legal/ImpressumPage';
import DatenschutzPage from '@/components/legal/DatenschutzPage';
import AGBPage from '@/components/legal/AGBPage';
import RisikoDisclaimerPage from '@/components/legal/RisikoDisclaimerPage';

interface AuthRouterProps {
  onLogin: (token: string) => void;
}

/**
 * Validates a redirect URL to prevent open redirect vulnerabilities.
 * Only allows relative URLs that start with '/' and don't contain protocol-relative URLs.
 */
function isValidRedirectUrl(url: string): boolean {
  if (!url || url.trim() === '') {
    return false;
  }

  // Must start with '/' (relative URL)
  if (!url.startsWith('/')) {
    return false;
  }

  // Reject protocol-relative URLs (e.g., '//evil.com')
  if (url.startsWith('//')) {
    return false;
  }

  // Reject URLs with protocols (e.g., 'http://', 'javascript:', 'data:')
  return !url.includes(':');


}

export default function AuthRouter({ onLogin }: AuthRouterProps) {
  const location = useLocation();

  // Known public routes that should not trigger a redirect parameter
  const publicPaths = [
    '/', '/login', '/auth/register', '/forgot-pw', '/impressum', '/datenschutz', '/agb', '/risikodisclaimer',
    FRAU_MEETS_BOERSE_CONFIG.routePath,
    `${FRAU_MEETS_BOERSE_CONFIG.routePath}${FRAU_MEETS_BOERSE_CONFIG.videosPath}`,
    `${FRAU_MEETS_BOERSE_CONFIG.routePath}/impressum`,
    `${FRAU_MEETS_BOERSE_CONFIG.routePath}/datenschutz`,
    `${FRAU_MEETS_BOERSE_CONFIG.routePath}/risikodisclaimer`
  ];

  // For any non-public route, redirect to login with the current path as redirect parameter
  const getRedirectUrl = () => {
    if (!publicPaths.includes(location.pathname)) {
      const redirectPath = location.pathname + location.search;
      // Only set redirect parameter if the URL is valid (security check)
      if (isValidRedirectUrl(redirectPath)) {
        return `/login?redirect=${encodeURIComponent(redirectPath)}`;
      }
    }
    return `/login${location.search}`;
  };

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<Login onLogin={onLogin} />} />
      <Route path="/auth/register" element={<Registration onRegister={onLogin} />} />
      <Route path="/forgot-pw" element={<ResetPassword />} />

      {/* Frau meets Börse Landingpage – accessible without authentication */}
      <Route path={FRAU_MEETS_BOERSE_CONFIG.routePath} element={<FrauMeetsBoersePage />} />
      {FmbSubRoutes()}

      {/* Legal pages – accessible without authentication */}
      <Route path="/impressum" element={<ImpressumPage />} />
      <Route path="/datenschutz" element={<DatenschutzPage />} />
      <Route path="/agb" element={<AGBPage />} />
      <Route path="/risikodisclaimer" element={<RisikoDisclaimerPage />} />

      {/* Redirect unknown paths to login with redirect parameter */}
      <Route path="*" element={<Navigate to={getRedirectUrl()} replace />} />
    </Routes>
  );
}
